import React, { useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

export const Context = React.createContext({})

//component - CoreUI / CSidebar
const CSidebar = props => {

  const {
    children,
    className,
    innerRef,
    breakpoint = 'lg',
    show = 'responsive',
    fixed = true,
    unfoldable,
    minimize,
    size,
    hideOnMobileClick = true,
    aside,
    colorScheme = 'dark',
    overlaid,
    dropdownMode,
    onShowChange,
    onMinimizeChange,
    ...attributes
  } = props

  const key = useState(Math.random().toString(36).substr(2))[0]

  const [isOpen, setIsOpen] = useState(show)
  const [openDropdown, setOpenDropdown] = useState()

  const node = useRef({}).current
  const reference = r => {
    node.current = r
    innerRef && innerRef(r)
  }

  const [minimized, setIsMinimized] = useState(minimize)
  useMemo(() => {
    setIsMinimized(minimize)
  }, [minimize])

  const toggleMinimize = () => {
    setIsMinimized(!minimized)
    onMinimizeChange && onMinimizeChange(minimized)
  }

  useMemo(() => {
    setIsOpen(show)
  }, [show])

  useEffect(() => {
    isOpen === true ? createBackdrop() : removeBackdrop()
    return () => removeBackdrop()
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarCloseListener = e => {
    if (
      document.getElementById(key + 'backdrop') &&
      !node.current.contains(e.target)
    ) {
      closeSidebar()
    }
  }

  const onKeydown = e => {
    e.key.includes('Esc') && isAutoclosable() && closeSidebar()
  }

  const createBackdrop = () => {
    const backdrop = document.createElement('div')
    if (overlaid) {
      document.addEventListener('click', sidebarCloseListener)
    } else {
      backdrop.addEventListener('click', closeSidebar)
    }
    document.addEventListener('keydown', onKeydown)
    backdrop.className = 'sidebar-backdrop c-show'
    backdrop.id = key + 'backdrop'
    document.body.appendChild(backdrop)
  }

  const removeBackdrop = () => {
    const backdrop = document.getElementById(key + 'backdrop')
    if (backdrop) {
      document.removeEventListener('click', sidebarCloseListener)
      backdrop.removeEventListener('click', closeSidebar)
      document.removeEventListener('keydown', onKeydown)
      document.body.removeChild(backdrop)
    }
  }

  const closeSidebar = () => {
    if (typeof onShowChange === 'function') {
      onShowChange(overlaid ? false : 'responsive')
    } else {
      setIsOpen(overlaid ? false : 'responsive')
    }
  }

  const isOnMobile = () => {
    return Boolean(getComputedStyle(node.current).getPropertyValue('--is-mobile'))
  }

  const isAutoclosable = () => isOnMobile() || overlaid

  const onSidebarClick = e => {
    const sidebarItemClicked = String(e.target.className).includes('sidebar-nav-link')
    if (
        sidebarItemClicked && 
        hideOnMobileClick &&
        isAutoclosable()
      ) {
      closeSidebar()
    }
  }

  const haveResponsiveClass = breakpoint && isOpen === 'responsive'
  const classes = classNames(
    'sidebar',
    colorScheme && `sidebar-${colorScheme}`,
    isOpen === true && 'sidebar-show',
    haveResponsiveClass && `sidebar-${breakpoint}-show`,
    fixed && !overlaid && 'sidebar-fixed',
    aside && 'sidebar-right',
    minimized && !unfoldable && 'sidebar-minimized',
    minimized && unfoldable && 'sidebar-unfoldable',
    overlaid && 'sidebar-overlaid',
    size && `sidebar-${size}`,
    className
  )

  return (
    <Context.Provider value={{
      dropdownMode,
      scrollbarExist: !minimized || unfoldable,
      toggleMinimize,
      openDropdown, 
      setOpenDropdown
    }}>
      <div
        className={classes}
        {...attributes}
        ref={reference}
        onClick={onSidebarClick}
      >
        {children}
      </div>
    </Context.Provider>
  )

}

CSidebar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  fixed: PropTypes.bool,
  unfoldable: PropTypes.bool,
  overlaid: PropTypes.bool,
  breakpoint: PropTypes.oneOf([false, '', 'sm', 'md', 'lg', 'xl', 'xxl']),
  minimize: PropTypes.bool,
  show: PropTypes.oneOf(['', true, false, 'responsive']),
  size: PropTypes.oneOf(['', 'sm', 'lg', 'xl']),
  hideOnMobileClick: PropTypes.bool,
  aside: PropTypes.bool,
  colorScheme: PropTypes.string,
  dropdownMode: PropTypes.oneOf([
    '', 'openActive', 'close', 'closeInactive', 'noAction'
  ]),
  onShowChange: PropTypes.func,
  onMinimizeChange: PropTypes.func
}

export default CSidebar
